import { isNumber } from "@foretagsplatsen/core-helpers/number";
import numbro from "../numbroWrapper.js";
import numbroWithFailsafe from "../numbroWithFailsafe.js";

const defaultFormat = ",0[.]00";

/**
 * Format `number` to a string with a thousand separator and two decimal.
 *
 * @param {number} number - The number to format
 * @return {string}
 */
export function formatCount(number) {
	return format(number, defaultFormat);
}

/**
 * Format `amount`, falling back to `fallback` value if parsing amount
 * fails.
 *
 * Numbro returns the string 'NaN' in such cases, which is not always the
 * intended behavior.
 */
export function formatWithFallback(amount, fallback = "") {
	if (!isNumber(amount)) {
		return fallback;
	}

	const formattedAmount = formatCount(amount);
	if (formattedAmount === "NaN") {
		return fallback;
	}

	return formattedAmount;
}

/**
 * Convenience method that reformats a string.
 *
 * This method is useful when parsing input number values, which are
 * strings, to format them.
 */
export function reformatNumberString(str) {
	return formatCount(numbro.unformat(str));
}

/**
 * Format `number` to a string with a thousand separator and no decimal.
 *
 * @param {number} number - The number to format
 * @return {string}
 */
export function formatNoDecimal(number) {
	return format(number, { mantissa: 0, thousandSeparated: true });
}

/**
 * Format `number` to a string with a thousand separator and two decimal.
 *
 * @param {number} number - The number to format
 * @return {string}
 */
export function formatForceTwoDecimals(number) {
	return format(number, { mantissa: 2, thousandSeparated: true });
}

function formatThousandNoDecimal(number) {
	return format(number, ",0 aK");
}

/**
 * Creates a number formatter with the specified number of decimals.
 *
 * @param {number} numberOfDecimals - The number of decimals to format
 * @return {function(number): string}
 */
export function withNumberOfDecimals(numberOfDecimals) {
	const numberFormat = ",0[.]".concat(
		new Array(numberOfDecimals + 1).join(0),
	);
	return (number) => format(number, numberFormat);
}

/**
 * Format `number` to a string with a thousand separator and no decimal and no
 * abbreviation at the end.
 *
 * @param {number} number - The number to format
 * @return {string}
 */
export function formatThousandWithoutAbbreviation(number) {
	const thousand = formatThousandNoDecimal(number);
	const abbreviation = numbro.languageData().abbreviations.thousand;
	return thousand.slice(0, -(abbreviation.length + 1));
}

function format(number, formatName) {
	return numbroWithFailsafe(number).format(formatName);
}
